/**
 * "Notify Me When Available" dialog (SHOP-627, redesigned in SHOP-726).
 *
 * The customer picks one notification channel - email or text message - and only that channel's
 * field is enabled and submitted. Validation problems are reported inline next to the field and
 * announced by moving focus to it; a failed request writes into a `role="alert"` region rather
 * than raising a native `alert()`.
 *
 * Listeners are delegated from `document` because a page can render more than one dialog and the
 * snippet that loads this module is rendered per product.
 */

const EMAIL_PATTERN = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
const PHONE_DIGITS = 10;

/** Remembers which element opened each dialog so focus can be handed back on close. */
const triggers = new WeakMap();

/**
 * @param {HTMLElement} dialog
 * @returns {HTMLInputElement | null} The radio for the channel the customer selected.
 */
function selectedChannelInput(dialog) {
  return dialog.querySelector('.bp-notify-me__channel-input:checked');
}

/**
 * @param {HTMLElement} dialog
 * @param {string} channel - `email` or `sms`.
 * @returns {HTMLInputElement | null} The contact field belonging to that channel.
 */
function channelField(dialog, channel) {
  return dialog.querySelector(`[data-bp-notify-panel="${channel}"] .bp-notify-me__input`);
}

/**
 * Shows the panel for the selected channel and hides the other one. The hidden panel's input is
 * disabled so it stays out of the tab order, out of the accessibility tree and out of the payload.
 *
 * @param {HTMLElement} dialog
 */
function syncChannel(dialog) {
  const selected = selectedChannelInput(dialog)?.value ?? 'email';

  for (const panel of dialog.querySelectorAll('[data-bp-notify-panel]')) {
    const isActive = panel.getAttribute('data-bp-notify-panel') === selected;
    const input = panel.querySelector('.bp-notify-me__input');

    panel.hidden = !isActive;
    if (input instanceof HTMLInputElement) {
      input.disabled = !isActive;
      if (!isActive) clearFieldError(input);
    }
  }

  clearFormError(dialog);
}

/**
 * @param {HTMLInputElement} input
 * @param {string | null} message - `null` clears the error.
 */
function setFieldError(input, message) {
  const errorEl = input.parentElement?.querySelector('.bp-notify-me__error');

  if (errorEl) {
    errorEl.textContent = message ?? '';
    errorEl.hidden = !message;
  }

  if (message) {
    input.setAttribute('aria-invalid', 'true');
  } else {
    input.removeAttribute('aria-invalid');
  }
}

/** @param {HTMLInputElement} input */
function clearFieldError(input) {
  setFieldError(input, null);
}

/** @param {HTMLElement} dialog */
function clearFormError(dialog) {
  const formError = dialog.querySelector('[data-bp-notify-form-error]');
  if (formError instanceof HTMLElement) {
    formError.textContent = '';
    formError.hidden = true;
  }
}

/**
 * @param {HTMLElement} dialog
 * @param {string} message
 */
function showFormError(dialog, message) {
  const formError = dialog.querySelector('[data-bp-notify-form-error]');
  if (formError instanceof HTMLElement) {
    formError.hidden = false;
    formError.textContent = message;
  }
}

/**
 * Puts the dialog back into its initial state: form visible, email channel selected, no errors.
 * Called on every open so a customer who already submitted does not reopen to a stale success
 * message - which would be wrong once they have switched to another variant.
 *
 * @param {HTMLElement} dialog
 */
function resetDialog(dialog) {
  const form = dialog.querySelector('.bp-notify-me__form');
  const success = dialog.querySelector('.bp-notify-me__success');

  if (form instanceof HTMLFormElement) {
    form.hidden = false;

    for (const input of form.querySelectorAll('.bp-notify-me__input')) {
      if (input instanceof HTMLInputElement) clearFieldError(input);
    }

    const firstChannel = form.querySelector('.bp-notify-me__channel-input');
    if (firstChannel instanceof HTMLInputElement) firstChannel.checked = true;

    setBusy(form, false);
  }

  if (success instanceof HTMLElement) success.hidden = true;

  syncChannel(dialog);
}

/**
 * @param {HTMLFormElement} form
 * @param {boolean} busy
 */
function setBusy(form, busy) {
  const submit = form.querySelector('[data-bp-notify-submit]');
  const label = form.querySelector('[data-bp-notify-submit-label]');

  if (!(submit instanceof HTMLButtonElement)) return;

  submit.disabled = busy;
  submit.setAttribute('aria-busy', String(busy));

  if (label instanceof HTMLElement) {
    if (busy) {
      label.dataset.idleLabel ??= label.textContent ?? '';
      label.textContent = form.dataset.submittingLabel ?? label.textContent;
    } else if (label.dataset.idleLabel) {
      label.textContent = label.dataset.idleLabel;
    }
  }
}

/**
 * @param {HTMLElement} dialog
 * @param {string} channel
 */
function showSuccess(dialog, channel) {
  const form = dialog.querySelector('.bp-notify-me__form');
  const success = dialog.querySelector('.bp-notify-me__success');
  const text = dialog.querySelector('[data-bp-notify-success-text]');

  if (text instanceof HTMLElement) {
    text.textContent = (channel === 'sms' ? text.dataset.smsMessage : text.dataset.emailMessage) ?? '';
  }

  if (form instanceof HTMLElement) form.hidden = true;

  if (success instanceof HTMLElement) {
    success.hidden = false;
    success.focus();
  }
}

/**
 * Closes the dialog and hands focus back to whatever opened it, as our accessibility standards
 * require. Every dismissal route goes through here rather than relying on the `close` event or on
 * the browser's own focus restoration - the former does not bubble, and the latter restores to
 * whatever was focused before `showModal()`, which is not the trigger once we move focus to the
 * title on open.
 *
 * @param {HTMLDialogElement} dialog
 */
function closeDialog(dialog) {
  const trigger = triggers.get(dialog);

  dialog.close();

  if (trigger instanceof HTMLElement && trigger.isConnected) trigger.focus();
}

document.addEventListener('click', (event) => {
  const target = event.target;
  if (!(target instanceof Element)) return;

  const trigger = target.closest('[data-notify-trigger]');
  if (trigger) {
    event.preventDefault();

    // The trigger can be a <button> (native disabled state, click never fires) or an
    // <a> (no disabled attribute, so bp-shipping-week.js flags it via aria-disabled).
    if (trigger.disabled || trigger.getAttribute('aria-disabled') === 'true') {
      return;
    }
    
    const dialog = document.getElementById(`notify-me-modal-${trigger.getAttribute('data-notify-trigger')}`);

    if (dialog instanceof HTMLDialogElement) {
      triggers.set(dialog, trigger);
      resetDialog(dialog);
      dialog.showModal();
      // Focus the title rather than the default first-focusable Close button, so screen reader
      // users hear what the dialog is about before its controls.
      dialog.querySelector('.bp-notify-me__title')?.focus();
    }
    return;
  }

  if (target.closest('[data-bp-notify-close]')) {
    const dialog = target.closest('dialog');
    if (dialog instanceof HTMLDialogElement) closeDialog(dialog);
    return;
  }

  // A click whose target is the <dialog> itself came from the backdrop (or the dialog's own
  // padding, outside .bp-notify-me__inner) - treat it as a request to dismiss.
  if (target instanceof HTMLDialogElement && target.classList.contains('bp-notify-me')) {
    closeDialog(target);
    return;
  }

  const recommendationsLink = target.closest('[data-scroll-to-recommendations]');
  if (recommendationsLink) {
    event.preventDefault();
    const recommendations = document.querySelector('.product-recommendations');
    if (recommendations instanceof HTMLElement) {
      const prefersReducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
      recommendations.scrollIntoView({ behavior: prefersReducedMotion ? 'auto' : 'smooth', block: 'start' });
      // Move focus along with the viewport, otherwise keyboard users carry on tabbing from the
      // link they just left and never reach what was scrolled into view.
      recommendations.setAttribute('tabindex', '-1');
      recommendations.focus({ preventScroll: true });
    }
  }
});

document.addEventListener('change', (event) => {
  const target = event.target;
  if (!(target instanceof HTMLInputElement) || !target.classList.contains('bp-notify-me__channel-input')) return;

  const dialog = target.closest('.bp-notify-me');
  if (dialog instanceof HTMLElement) syncChannel(dialog);
});

// Take over Escape so that dismissing with the keyboard runs the same focus restoration as the
// Close button and the backdrop, instead of leaving focus on <body>.
document.addEventListener('keydown', (event) => {
  if (event.key !== 'Escape') return;

  const dialog = event.target instanceof Element ? event.target.closest('dialog.bp-notify-me') : null;
  if (!(dialog instanceof HTMLDialogElement) || !dialog.open) return;

  event.preventDefault();
  closeDialog(dialog);
});

document.addEventListener('input', (event) => {
  const target = event.target;
  if (!(target instanceof HTMLInputElement) || !target.classList.contains('bp-notify-me__input')) return;

  if (target.getAttribute('aria-invalid') === 'true') clearFieldError(target);
});

document.addEventListener('submit', (event) => {
  const form = event.target;
  if (!(form instanceof HTMLFormElement) || !form.classList.contains('bp-notify-me__form')) return;

  event.preventDefault();

  const dialog = form.closest('.bp-notify-me');
  if (!(dialog instanceof HTMLElement)) return;

  const channel = selectedChannelInput(dialog)?.value ?? 'email';
  const field = channelField(dialog, channel);
  if (!(field instanceof HTMLInputElement)) return;

  clearFormError(dialog);
  clearFieldError(field);

  const value = field.value.trim();
  const phoneDigits = value.replace(/\D/g, '');
  const isValid = channel === 'sms' ? phoneDigits.length === PHONE_DIGITS : EMAIL_PATTERN.test(value);

  if (!isValid) {
    setFieldError(field, field.dataset.errorMessage ?? '');
    field.focus();
    return;
  }

  const data = Object.fromEntries(new FormData(form).entries());
  delete data.notify_channel;
  // Always send both contact keys so the payload shape stays the same whichever channel was
  // chosen; the field that was not used is submitted empty.
  data.email = channel === 'email' ? value : '';
  data.phone_number = channel === 'sms' ? phoneDigits : '';
  // FormData values are always strings; the proxy rejects timestamp unless it's a JSON integer.
  data.timestamp = parseInt(String(data.timestamp), 10) || Math.floor(Date.now() / 1000);

  setBusy(form, true);

  fetch(String(form.dataset.proxyUrl), {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      Accept: 'application/json',
    },
    body: JSON.stringify(data),
  })
    .then((response) => {
      if (!response.ok) throw new Error(`Notify Me request failed with status ${response.status}`);
      showSuccess(dialog, channel);
    })
    .catch((error) => {
      console.error(error);
      showFormError(dialog, form.dataset.genericError ?? '');
    })
    .finally(() => {
      setBusy(form, false);
    });
});
